import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { CheckCircle, Download, MapPin, Bell, Smartphone, ShieldCheck, Coffee, Wifi, Heart, Users, Mail, Facebook, Instagram, Twitter, Youtube, Calendar, Award, HelpCircle, Accessibility } from 'lucide-react';
import Navigation from '../components/Navigation';

const AttendeeGuidePage = () => {
  const [newsletterEmail, setNewsletterEmail] = useState('');

  const beforeArriveCards = [
    {
      icon: CheckCircle,
      title: 'Check Your Registration',
      description: 'Ensure your badge is activated and ready for pickup.'
    },
    {
      icon: MapPin,
      title: 'Plan Your Stay',
      description: 'Explore hotel and travel options near the venue.'
    },
    {
      icon: Download,
      title: 'Pack Smart',
      description: 'Bring essentials for three days of learning and fun.'
    },
    {
      icon: Smartphone,
      title: 'Download the App',
      description: 'Stay connected with schedules and real-time updates.'
    }
  ];

  const quickLinks = [
    { icon: MapPin, title: 'Hotel & Travel', href: '#travel' },
    { icon: Accessibility, title: 'ADA Assistance', href: '#ada' },
    { icon: HelpCircle, title: 'FAQs', href: '#faq' },
    { icon: Award, title: 'Badge Activation', href: '#badge' }
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    alert('Newsletter subscription submitted!');
    setNewsletterEmail('');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Header */}
      <div className="relative pt-32 pb-20 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0B1B3B]/5 via-white to-white">
          <div className="absolute inset-0 opacity-5">
            <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
              <pattern id="crowd-silhouette" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="#0B1B3B" />
                <circle cx="25" cy="75" r="1.5" fill="#0B1B3B" />
                <circle cx="75" cy="25" r="1" fill="#0B1B3B" />
              </pattern>
              <rect width="100%" height="100%" fill="url(#crowd-silhouette)" />
            </svg>
          </div>
        </div>

        <div className="relative max-w-[1440px] mx-auto px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-6">
            <a href="/" className="hover:text-[#CFA64B] transition-colors">Home</a>
            <span>/</span>
            <span className="text-gray-600">Conference Info</span>
            <span>/</span>
            <span className="text-[#0B1B3B] font-medium">Attendee Guide</span>
          </div>

          {/* Title */}
          <h1 className="text-5xl md:text-6xl font-bold text-[#0B1B3B] mb-4 leading-tight">
            Attendee Guide
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mb-8">
            Everything you need to know before stepping into the KNOWN CON experience.
          </p>

          {/* Quick Links Bar */}
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" className="border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-[#0B1B3B]">
              Hotel & Travel
            </Button>
            <Button variant="outline" className="border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-[#0B1B3B]">
              ADA Assistance
            </Button>
            <Button variant="outline" className="border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-[#0B1B3B]">
              FAQs
            </Button>
            <Button className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] font-semibold">
              Buy Tickets
            </Button>
          </div>
        </div>
      </div>

      {/* Welcome & Overview */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-6">Welcome to the Movement</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-8">
              You're about to step into three transformative days where faith meets culture and purpose. 
              KNOWN CON isn't just a conference—it's a gathering of believers, leaders, and change-makers united by one mission: 
              to be known for making an impact. Get ready for powerful worship, life-changing teaching, and connections that last a lifetime.
            </p>
            <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-semibold px-8 py-6 text-lg">
              View Experience Overview
            </Button>
          </div>

          {/* Image Strip */}
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {[
              'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1511632765486-a01980e01a18?w=600&h=400&fit=crop',
              'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=600&h=400&fit=crop'
            ].map((img, idx) => (
              <div key={idx} className="relative overflow-hidden rounded-2xl aspect-[4/3] shadow-lg hover:shadow-2xl transition-all duration-500 group">
                <img
                  src={img}
                  alt={`Event ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-[#0B1B3B]/80 transition-all duration-500" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Before You Arrive */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Before You Arrive</h2>
            <p className="text-xl text-gray-600">Get ready for an unforgettable experience</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {beforeArriveCards.map((card, idx) => {
              const Icon = card.icon;
              return (
                <Card
                  key={idx}
                  className="border-none shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-3 group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg">
                      <Icon className="w-10 h-10 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-3">{card.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{card.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* On-Site Experience */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-16 text-center">When You're Here</h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div className="relative overflow-hidden rounded-3xl shadow-2xl aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=800&h=600&fit=crop"
                alt="On-site experience"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3B]/60 via-transparent to-transparent" />
            </div>

            {/* Right: Info */}
            <div className="space-y-8">
              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#CFA64B]/10 flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-6 h-6 text-[#CFA64B]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">Registration & Check-In</h3>
                    <p className="text-gray-700">Pick up your badge at the registration desk. Have your confirmation email ready for quick check-in.</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#CFA64B]/10 flex items-center justify-center flex-shrink-0">
                    <ShieldCheck className="w-6 h-6 text-[#CFA64B]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">Security & Bag Policy</h3>
                    <p className="text-gray-700">For everyone's safety, bags will be checked at entry. Please allow extra time and pack light.</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#CFA64B]/10 flex items-center justify-center flex-shrink-0">
                    <Coffee className="w-6 h-6 text-[#CFA64B]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">Food & Refreshments</h3>
                    <p className="text-gray-700">Multiple food vendors and cafes available throughout the venue. Water stations on every floor.</p>
                  </div>
                </div>
              </div>

              <div>
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#CFA64B]/10 flex items-center justify-center flex-shrink-0">
                    <Wifi className="w-6 h-6 text-[#CFA64B]" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-[#0B1B3B] mb-2">Wi-Fi Access</h3>
                    <p className="text-gray-700">Free Wi-Fi available. Network: KNOWNCON | Password: faith2025</p>
                  </div>
                </div>
              </div>

              <Button variant="outline" className="border-[#CFA64B] text-[#CFA64B] hover:bg-[#CFA64B] hover:text-[#0B1B3B] font-semibold">
                <MapPin className="w-4 h-4 mr-2" />
                View Venue Map
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Code of Conduct */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-16 text-center">Our Culture of Respect</h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <p className="text-lg text-gray-700 leading-relaxed mb-8">
                KNOWN CON is a space where everyone belongs. We're committed to creating an environment 
                where attendees can grow in faith, build authentic relationships, and experience God's love through community.
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#CFA64B] flex items-center justify-center flex-shrink-0 mt-1">
                    <Heart className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Lead with kindness and respect</h3>
                    <p className="text-gray-600">Treat every person with dignity, honor, and compassion.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#CFA64B] flex items-center justify-center flex-shrink-0 mt-1">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Celebrate diversity and inclusion</h3>
                    <p className="text-gray-600">We welcome people from all backgrounds, cultures, and walks of life.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#CFA64B] flex items-center justify-center flex-shrink-0 mt-1">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-[#0B1B3B] mb-2">Keep faith and purpose at the heart</h3>
                    <p className="text-gray-600">Every conversation should uplift, inspire, and point toward greater impact.</p>
                  </div>
                </div>
              </div>

              <Button variant="outline" className="border-[#0B1B3B] text-[#0B1B3B] hover:bg-[#0B1B3B] hover:text-white mt-8">
                Read Full Policy
              </Button>
            </div>

            {/* Right: Image */}
            <div className="relative overflow-hidden rounded-3xl shadow-2xl aspect-[4/3]">
              <img
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop"
                alt="Community togetherness"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B1B3B]/40 via-transparent to-transparent" />
            </div>
          </div>
        </div>
      </div>

      {/* Accessibility Banner */}
      <div className="py-16 bg-gradient-to-r from-[#CFA64B] to-[#d97706]">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4 text-[#0B1B3B]">
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Accessibility className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-bold mb-1">We're committed to making KNOWN CON accessible for everyone.</h3>
                <p className="text-[#0B1B3B]/80">Assistance services available for all attendees</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white font-semibold">
                ADA Assistance
              </Button>
              <Button variant="outline" className="bg-white/10 hover:bg-white/20 text-[#0B1B3B] border-[#0B1B3B]/20">
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stay Connected */}
      <div className="py-20" style={{ backgroundColor: '#FFF8E5' }}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Stay Connected</h2>
            <p className="text-xl text-gray-600">Be part of the movement year-round</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Join Group Chat */}
            <Card className="border-2 border-[#CFA64B]/20 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-full flex items-center justify-center">
                  <Users className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">Join the Group Chat</h3>
                <p className="text-gray-600 mb-6">Connect with fellow attendees and continue the conversation.</p>
                <Button className="bg-[#0B1B3B] hover:bg-[#1a2f5a] text-white w-full">
                  Join Now
                </Button>
              </CardContent>
            </Card>

            {/* Newsletter */}
            <Card className="border-2 border-[#CFA64B]/20 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-full flex items-center justify-center">
                  <Bell className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">Subscribe for Updates</h3>
                <p className="text-gray-600 mb-6">Get the latest news and exclusive content.</p>
                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="Your email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    required
                    className="border-gray-300"
                  />
                  <Button type="submit" className="bg-[#CFA64B] hover:bg-[#BD954A] text-[#0B1B3B] w-full font-semibold">
                    Subscribe
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Social Media */}
            <Card className="border-2 border-[#CFA64B]/20 hover:border-[#CFA64B] hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-gradient-to-br from-[#CFA64B] to-[#d97706] rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-[#0B1B3B] mb-3">Follow Us Live</h3>
                <p className="text-gray-600 mb-6">Stay connected on social media for live updates.</p>
                <div className="flex justify-center gap-4">
                  <a href="#" className="w-12 h-12 rounded-full bg-[#0B1B3B] hover:bg-[#CFA64B] flex items-center justify-center transition-colors">
                    <Facebook className="w-5 h-5 text-white" />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-[#0B1B3B] hover:bg-[#CFA64B] flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5 text-white" />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-[#0B1B3B] hover:bg-[#CFA64B] flex items-center justify-center transition-colors">
                    <Twitter className="w-5 h-5 text-white" />
                  </a>
                  <a href="#" className="w-12 h-12 rounded-full bg-[#0B1B3B] hover:bg-[#CFA64B] flex items-center justify-center transition-colors">
                    <Youtube className="w-5 h-5 text-white" />
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Quick Links Grid */}
      <div className="py-20 bg-white">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#0B1B3B] mb-4">Useful Resources</h2>
            <p className="text-xl text-gray-600">Quick access to everything you need</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {quickLinks.map((link, idx) => {
              const Icon = link.icon;
              return (
                <a
                  key={idx}
                  href={link.href}
                  className="block p-8 bg-white border-2 border-gray-200 hover:border-[#CFA64B] rounded-2xl transition-all duration-300 hover:shadow-xl group"
                >
                  <div className="w-14 h-14 mx-auto mb-4 bg-[#CFA64B]/10 group-hover:bg-[#CFA64B] rounded-full flex items-center justify-center transition-colors">
                    <Icon className="w-7 h-7 text-[#CFA64B] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0B1B3B] text-center group-hover:text-[#CFA64B] transition-colors">
                    {link.title}
                  </h3>
                </a>
              );
            })}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1B3B] via-[#1a2f5a] to-[#CFA64B]" />
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-white rounded-full filter blur-3xl"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center text-white">
          <h2 className="text-4xl md:text-6xl font-bold mb-6">
            Your KNOWN CON journey begins soon — see you there!
          </h2>
          <p className="text-xl mb-10 text-gray-200">
            Get ready for three transformative days
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button className="bg-white text-[#0B1B3B] hover:bg-gray-100 font-bold px-10 py-6 text-lg shadow-xl">
              Buy Tickets
            </Button>
            <Button variant="outline" className="bg-transparent border-white text-white hover:bg-white/10 border-2 px-10 py-6 text-lg">
              View Experience Overview
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f172a] text-white py-12">
        <div className="max-w-[1440px] mx-auto px-6 text-center">
          <p className="text-gray-400 text-sm">© 2025 KNOWN CON. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default AttendeeGuidePage;
